import {
  tileDef,
  unitDef,
  type PlayerId,
  type ProductionBuildingKind,
  type UnitKind,
} from "@dailywars/core/engine";
import { useState } from "react";

import { InfotipProvider } from "#/components/ui/infotip";
import { cn } from "#/lib/utils";

import { CoinMoneyIcon } from "./CoinMoneyIcon";
import {
  gameDockedPanelSurfaceClassName,
  gameDockedPanelTitleClassName,
} from "./GameDockedPanelShell";
import { BuildOptionsList } from "./ProductionBuildMenuRows";
import { BuildMenuFooter, useProductionBuildMenuHotkeys } from "./ProductionBuildMenuShared";
import type { BuildOption } from "./productionBuildMenuTypes";
import { ProductionBuildMenuUnitDetail } from "./ProductionBuildMenuUnitDetail";

interface ProductionBuildMenuProps {
  buildingKind: ProductionBuildingKind;
  funds: number;
  options: BuildOption[];
  owner: PlayerId;
  onChoose: (kind: UnitKind) => void;
  onCancel: () => void;
  className?: string;
}

function opponentPlayerId(owner: PlayerId): PlayerId {
  return owner === "p1" ? "p2" : "p1";
}

function BuildMenuHeader({ buildingName, funds }: { buildingName: string; funds: number }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <p className={gameDockedPanelTitleClassName}>{buildingName}</p>
      <div className="flex shrink-0 items-center gap-3 text-sm tabular-nums">
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          {funds}
          <CoinMoneyIcon size="sm" />
        </span>
      </div>
    </div>
  );
}

function ProductionBuildListView({
  buildingKind,
  funds,
  options,
  owner,
  onChoose,
  onCancel,
  onInspect,
}: Omit<ProductionBuildMenuProps, "className"> & { onInspect: (kind: UnitKind) => void }) {
  const buildingName = tileDef(buildingKind).name;

  return (
    <>
      <BuildMenuHeader buildingName={buildingName} funds={funds} />
      <BuildOptionsList options={options} owner={owner} onChoose={onChoose} onInspect={onInspect} />
      <BuildMenuFooter onCancel={onCancel} />
    </>
  );
}

function ProductionBuildMenuBody({
  buildingKind,
  funds,
  options,
  owner,
  onChoose,
  onCancel,
  className,
}: ProductionBuildMenuProps) {
  const [inspectedUnitKind, setInspectedUnitKind] = useState<UnitKind | null>(null);
  const buildingName = tileDef(buildingKind).name;
  const inspectedOption =
    inspectedUnitKind === null
      ? null
      : (options.find((option) => option.kind === inspectedUnitKind) ?? null);

  useProductionBuildMenuHotkeys({ options, onChoose, enabled: inspectedUnitKind === null });

  return (
    <div
      role="dialog"
      aria-label={
        inspectedOption
          ? `${unitDef(inspectedOption.kind).name} unit details`
          : `Build unit at ${buildingName}`
      }
      className={cn(gameDockedPanelSurfaceClassName, "w-full", className)}
    >
      {inspectedOption ? (
        <ProductionBuildMenuUnitDetail
          option={inspectedOption}
          enemyOwner={opponentPlayerId(owner)}
          onBack={() => setInspectedUnitKind(null)}
          onCancel={onCancel}
          onChoose={onChoose}
        />
      ) : (
        <ProductionBuildListView
          buildingKind={buildingKind}
          funds={funds}
          options={options}
          owner={owner}
          onChoose={onChoose}
          onCancel={onCancel}
          onInspect={setInspectedUnitKind}
        />
      )}
    </div>
  );
}

export function ProductionBuildMenu(props: ProductionBuildMenuProps) {
  return (
    <InfotipProvider delay={50}>
      <ProductionBuildMenuBody {...props} />
    </InfotipProvider>
  );
}
